import React, { useEffect, useState, useCallback, useRef } from 'react';
import oxiDequeueService from '../services/oxiDequeueService';
import './OxiDequeueAdmin.css';

const PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 3000;

// THE INSTRUCTIONS THIS HUB SENDS TO A SOURCE SYSTEM — queued, and everything already sent.
//
// Two lives. On /admin/oxi-dequeue-queue it is the whole table across every project, with the filters
// and the destructive tools an operator needs. Given a `syncProjectId` it becomes one project's
// "Instructions to Source" tab: the same rows, the same detail modal, scoped to that project and with
// nothing on it that deletes.
//
// PARAMETERISED RATHER THAN COPIED. The value here is the detail view — the exact request message sent
// (with its request id), the response status, the response body, the instruction behind it. A second
// implementation of that on the project page would be the same screen drifting away from itself, and
// the half that got the next fix would be whichever one the next ticket came in on.
//
// @param {number|string} [syncProjectId] scope every list to one project, and hide what does not belong
//                                        on a project's own page (the page header, the project filter,
//                                        delete and purge).
export default function OxiDequeueAdmin({ syncProjectId = null }) {
    // "Scoped" is the embedded mode: one project, read-only.
    const scoped = syncProjectId !== null && syncProjectId !== undefined && syncProjectId !== '';
    const [rows, setRows]               = useState([]);
    const [total, setTotal]             = useState(0);
    const [page, setPage]               = useState(1);
    const [messageTypes, setMessageTypes] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [message, setMessage]         = useState(null);
    const [confirm, setConfirm]         = useState(null);
    const [deleting, setDeleting]       = useState(null);
    const [purging, setPurging]         = useState(false);
    const [filters, setFilters]         = useState({ syncProjectId: scoped ? String(syncProjectId) : '', messageType: '' });
    const [pendingFilters, setPendingFilters] = useState({ syncProjectId: scoped ? String(syncProjectId) : '', messageType: '' });
    const [tab, setTab]                 = useState('queue'); // 'queue' | 'history'
    const [detail, setDetail]           = useState(null);    // history row opened for detail view
    const [copied, setCopied]           = useState(null);    // which field was just copied ('req' | 'resp')
    const [instructionView, setInstructionView] = useState(null); // read-only instruction modal
    // AUTO-REFRESH, OFF BY DEFAULT. On, it reloads the current tab and page every 3 seconds — for watching an
    // instruction go out and its answer come back without pressing Refresh. Quietly: no "Loading…" swap of
    // the table each tick, and a tick is skipped while the previous one is still in flight.
    const [autoRefresh, setAutoRefresh] = useState(false);
    const inFlight = useRef(false);

    const copy = async (text, key) => {
        try {
            await navigator.clipboard.writeText(text || '');
            setCopied(key);
            setTimeout(() => setCopied(c => (c === key ? null : c)), 1500);
        } catch (e) {
            setMessage({ type: 'error', text: 'Copy failed — clipboard unavailable' });
        }
    };

    const load = useCallback(async (pg = page, f = filters, silent = false) => {
        if (silent && inFlight.current) return;
        inFlight.current = true;
        if (!silent) setLoading(true);
        setError(null);
        try {
            const params = { page: pg, limit: PAGE_SIZE };
            if (f.syncProjectId) params.syncProjectId = f.syncProjectId;
            if (f.messageType)   params.messageType   = f.messageType;
            const res = tab === 'history'
                ? await oxiDequeueService.listHistory(params)
                : await oxiDequeueService.list(params);
            const data = res.data || res;
            setRows(data.rows || []);
            setTotal(data.total || 0);
            if (data.messageTypes) setMessageTypes(data.messageTypes);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            inFlight.current = false;
            if (!silent) setLoading(false);
        }
    }, [page, filters, tab]);

    useEffect(() => { load(page, filters); }, [page, filters, tab]);

    useEffect(() => {
        if (!autoRefresh) return undefined;
        const id = setInterval(() => load(page, filters, true), AUTO_REFRESH_MS);
        return () => clearInterval(id);
    }, [autoRefresh, load, page, filters]);

    const autoRefreshToggle = (
        <label className="odq-auto-refresh" title="Reload this list every 3 seconds">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh (3s)
        </label>
    );

    // THE SCOPE IS NOT A FILTER THE USER SET, so it is re-applied whenever the project changes rather
    // than left as whatever was typed. Without this, moving between two projects on the same mounted
    // component would show the first project's instructions under the second one's heading.
    useEffect(() => {
        if (!scoped) return;
        const id = String(syncProjectId);
        setFilters(f => (f.syncProjectId === id ? f : { ...f, syncProjectId: id }));
        setPendingFilters(f => (f.syncProjectId === id ? f : { ...f, syncProjectId: id }));
        setPage(1);
    }, [scoped, syncProjectId]);

    const selectTab = (t) => { if (t === tab) return; setTab(t); setPage(1); setConfirm(null); };

    // Open the detail modal, fetching the full response body (the list omits it to stay light).
    const openDetail = async (r) => {
        setDetail({ ...r, _loading: true });
        try {
            const res = await oxiDequeueService.historyDetail(r.id);
            const full = (res.data || res).row;
            setDetail(full ? { ...r, ...full, _loading: false } : { ...r, _loading: false });
        } catch (e) {
            setDetail({ ...r, _loading: false, _error: e.response?.data?.message || e.message });
        }
    };

    const applyFilters = () => {
        setFilters(pendingFilters);
        setPage(1);
    };

    const clearFilters = () => {
        const empty = { syncProjectId: scoped ? String(syncProjectId) : '', messageType: '' };
        setPendingFilters(empty);
        setFilters(empty);
        setPage(1);
    };

    const handleDelete = async (id) => {
        if (confirm !== id) { setConfirm(id); return; }
        setDeleting(id);
        setConfirm(null);
        try {
            await oxiDequeueService.remove(id);
            setMessage({ type: 'success', text: `Row ${id} deleted.` });
            load(page, filters);
        } catch (e) {
            setMessage({ type: 'error', text: e.response?.data?.message || e.message });
        } finally {
            setDeleting(null);
        }
    };

    const handlePurge = async () => {
        const label = tab === 'history' ? 'history' : 'queue';
        const scoped = filters.syncProjectId || filters.messageType;
        const scopeText = scoped
            ? `the filtered ${label} records (${[filters.syncProjectId && `project ${filters.syncProjectId}`, filters.messageType].filter(Boolean).join(', ')})`
            : `ALL ${label} records`;
        if (!window.confirm(`Purge ${scopeText}? This permanently deletes them and cannot be undone.`)) return;
        setPurging(true);
        setMessage(null);
        try {
            const params = {};
            if (tab === 'history') params.history = 1;
            if (filters.syncProjectId) params.syncProjectId = filters.syncProjectId;
            if (filters.messageType)   params.messageType   = filters.messageType;
            const res = await oxiDequeueService.purge(params);
            const deleted = (res.data || res).deleted;
            setMessage({ type: 'success', text: `Purged ${deleted != null ? deleted : ''} ${label} record(s).` });
            setPage(1);
            load(1, filters);
        } catch (e) {
            setMessage({ type: 'error', text: e.response?.data?.message || e.message });
        } finally {
            setPurging(false);
        }
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <div className={scoped ? 'odq-embedded' : 'acp-page'}>
            {!scoped && <div className="acp-header">
                <div>
                    <h1 className="acp-title">OXI Dequeue Queue</h1>
                    <p className="acp-subtitle">
                        {total.toLocaleString()} {tab === 'history' ? 'handed over' : 'waiting to be collected'} in <code>syncconnect-instructions-*</code>
                        {' '}(OpenSearch, one index per week, each deleted 35 days after it starts)
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {autoRefreshToggle}
                    <button className="acp-btn acp-btn-secondary" onClick={() => load(page, filters)} disabled={loading}>
                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                            <path d="M4 10a6 6 0 0 1 6-6 6 6 0 0 1 4.24 1.76L16 4v4h-4l1.42-1.42A4 4 0 1 0 14 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {loading ? 'Loading…' : 'Refresh'}
                    </button>
                    <button className="acp-btn acp-btn-danger" onClick={handlePurge} disabled={purging || loading || total === 0}>
                        {purging ? 'Purging…' : (filters.syncProjectId || filters.messageType) ? 'Purge filtered' : 'Purge all'}
                    </button>
                </div>
            </div>}

            <div className="odq-tabs">
                <button className={`odq-tab ${tab === 'queue' ? 'active' : ''}`} onClick={() => selectTab('queue')}>Queue</button>
                <button className={`odq-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => selectTab('history')}>History</button>
            </div>

            {message && (
                <div className={`os-msg os-msg-${message.type}`} onClick={() => setMessage(null)}>
                    {message.text}
                </div>
            )}

            {error && <div className="os-msg os-msg-error">{error}</div>}

            <div className="acp-card">
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">
                        {scoped
                            ? `${tab === 'history' ? 'Sent' : 'Queued'} — ${total.toLocaleString()}`
                            : 'Queue Entries'}
                    </span>
                    <div className="odq-filters">
                        {/* The project is the page, not a filter, so it is not offered as one here. */}
                        {!scoped && <input
                            className="odq-filter-input"
                            placeholder="Sync project ID…"
                            value={pendingFilters.syncProjectId}
                            onChange={e => setPendingFilters(f => ({ ...f, syncProjectId: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && applyFilters()}
                        />}
                        <select
                            className="odq-filter-select"
                            value={pendingFilters.messageType}
                            onChange={e => setPendingFilters(f => ({ ...f, messageType: e.target.value }))}
                        >
                            <option value="">All types</option>
                            {messageTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={applyFilters}>Search</button>
                        {((scoped ? filters.messageType : (filters.syncProjectId || filters.messageType))) && (
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={clearFilters}>Clear</button>
                        )}
                        {scoped && (
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => load(page, filters)} disabled={loading}>
                                {loading ? 'Loading…' : 'Refresh'}
                            </button>
                        )}
                        {scoped && autoRefreshToggle}
                    </div>
                </div>

                {loading ? (
                    <div className="acp-empty">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="acp-empty">No records found.</div>
                ) : tab === 'history' ? (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Queue ID</th>
                                {!scoped && <th>Sync Project</th>}
                                <th>Message Type</th>
                                <th>SPR Record UUID</th>
                                <th>Response</th>
                                <th>Archived</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="odq-clickable" onClick={() => openDetail(r)} title="View response details">
                                    <td style={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.original_id ?? r.id}</td>
                                    {!scoped && <td>{r.sync_project_id}</td>}
                                    <td>
                                        {r.message_type
                                            ? <span className="odq-type-pill">{r.message_type}</span>
                                            : <span style={{ color: '#d1d5db' }}>—</span>}
                                    </td>
                                    <td className="odq-uuid" title={r.spr_record_uuid}>{r.spr_record_uuid || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                    <td>
                                        {r.response_status ? (
                                            <span className="odq-resp-cell">
                                                <span className={`odq-resp-badge ${r.response_status === 'SUCCESS' ? 'ok' : 'warn'}`}>{r.response_status}</span>
                                                {r.response_message && (
                                                    <span className="odq-resp-msg" title={r.response_message}>{r.response_message}</span>
                                                )}
                                            </span>
                                        ) : (
                                            <span style={{ color: '#9ca3af' }}>Awaiting response</span>
                                        )}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.8rem' }}>
                                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                {!scoped && <th>Sync Project</th>}
                                <th>Message Type</th>
                                <th>S3 Key</th>
                                <th>SPR Record UUID</th>
                                <th>Instruction</th>
                                <th>Created</th>
                                {!scoped && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id}>
                                    <td style={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.id}</td>
                                    {!scoped && <td>{r.sync_project_id}</td>}
                                    <td>
                                        {r.message_type
                                            ? <span className="odq-type-pill">{r.message_type}</span>
                                            : <span style={{ color: '#d1d5db' }}>—</span>}
                                    </td>
                                    <td className="odq-s3key" title={r.s3_key}>{r.s3_key || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                    <td className="odq-uuid" title={r.spr_record_uuid}>{r.spr_record_uuid || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                    <td style={{ color: '#6b7280' }}>{r.instruction_id || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.8rem' }}>
                                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                                    </td>
                                    {/* NOTHING DESTRUCTIVE ON THE PROJECT'S OWN TAB. It is there to answer
                                        "what did we ask the source for, and what came back" — deleting a
                                        queued instruction from it is a different job, and it is on the
                                        admin screen where an operator goes looking for it. */}
                                    {!scoped && <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {confirm === r.id ? (
                                            <span className="os-confirm-row">
                                                <span className="os-confirm-text">Delete?</span>
                                                <button
                                                    className="acp-btn acp-btn-danger acp-btn-sm"
                                                    onClick={() => handleDelete(r.id)}
                                                    disabled={deleting === r.id}
                                                >
                                                    {deleting === r.id ? '…' : 'Yes'}
                                                </button>
                                                <button
                                                    className="acp-btn acp-btn-secondary acp-btn-sm"
                                                    onClick={() => setConfirm(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </span>
                                        ) : (
                                            <button
                                                className="acp-btn acp-btn-danger acp-btn-sm"
                                                onClick={() => handleDelete(r.id)}
                                                disabled={!!deleting}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {total > 0 && (
                    <div className="odq-pagination">
                        <button
                            className="acp-btn acp-btn-secondary acp-btn-sm"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1 || loading}
                        >
                            Prev
                        </button>
                        <span className="odq-page-label">
                            Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()} · Page {page} of {Math.max(totalPages, 1)}
                        </span>
                        <button
                            className="acp-btn acp-btn-secondary acp-btn-sm"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages || loading}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {detail && (
                <div className="odq-modal-overlay" onClick={() => setDetail(null)}>
                    <div className="odq-modal" onClick={e => e.stopPropagation()}>
                        <div className="odq-modal-head">
                            <span>Dequeue history — queue id {detail.original_id ?? detail.id}</span>
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setDetail(null)}>Close</button>
                        </div>
                        <div className="odq-modal-body">
                            <dl className="odq-detail">
                                <dt>Request message</dt>
                                <dd>{detail._loading
                                    ? <span style={{ color: '#9ca3af' }}>Loading…</span>
                                    : (detail.request_message || detail.instruction_content)
                                        ? <>
                                            <div className="odq-copy-row">
                                                <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => copy(detail.request_message || detail.instruction_content, 'req')}>
                                                    {copied === 'req' ? '✓ Copied' : 'Copy request'}
                                                </button>
                                            </div>
                                            <pre className="odq-detail-pre odq-detail-body">{detail.request_message || detail.instruction_content}</pre>
                                            {!detail.request_message && detail.instruction_content &&
                                                <span className="odq-note">Instruction template — the exact sent message (with request id) wasn’t captured for this record.</span>}
                                        </>
                                        : <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>Response status</dt>
                                <dd>
                                    {detail.response_status
                                        ? <span className={`odq-resp-badge ${detail.response_status === 'SUCCESS' ? 'ok' : 'warn'}`}>{detail.response_status}</span>
                                        : <span style={{ color: '#9ca3af' }}>Awaiting response</span>}
                                </dd>
                                <dt>Response message</dt>
                                <dd>{detail.response_message
                                    ? <pre className="odq-detail-pre">{detail.response_message}</pre>
                                    : <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>Response body</dt>
                                <dd>{detail._loading
                                    ? <span style={{ color: '#9ca3af' }}>Loading…</span>
                                    : detail._error
                                        ? <span className="os-msg os-msg-error" style={{ display: 'block' }}>{detail._error}</span>
                                        : detail.response_body
                                            ? <>
                                                <div className="odq-copy-row">
                                                    <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => copy(detail.response_body, 'resp')}>
                                                        {copied === 'resp' ? '✓ Copied' : 'Copy response'}
                                                    </button>
                                                </div>
                                                <pre className="odq-detail-pre odq-detail-body">{detail.response_body}</pre>
                                            </>
                                            : <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>Responded at</dt>
                                <dd>{detail.responded_at ? new Date(detail.responded_at).toLocaleString() : <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>Queue id</dt>
                                <dd>{detail.original_id ?? detail.id}</dd>
                                <dt>Sync project</dt>
                                <dd>{detail.sync_project_id}</dd>
                                <dt>Message type</dt>
                                <dd>{detail.message_type || <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>SPR record UUID</dt>
                                <dd style={{ wordBreak: 'break-all' }}>{detail.spr_record_uuid || <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>S3 key</dt>
                                <dd style={{ wordBreak: 'break-all' }}>{detail.s3_key || <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>Instruction</dt>
                                <dd>{detail.instruction_id
                                    ? <button className="odq-link-btn" onClick={() => setInstructionView(detail)}>
                                        {detail.instruction_title || `Instruction ${detail.instruction_id}`}
                                    </button>
                                    : <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                                <dt>Archived</dt>
                                <dd>{detail.created_at ? new Date(detail.created_at).toLocaleString() : <span style={{ color: '#9ca3af' }}>—</span>}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            )}

            {instructionView && (
                <div className="odq-modal-overlay" style={{ zIndex: 1001 }} onClick={() => setInstructionView(null)}>
                    <div className="odq-modal" onClick={e => e.stopPropagation()}>
                        <div className="odq-modal-head">
                            <span>{instructionView.instruction_title || `Instruction ${instructionView.instruction_id}`}</span>
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setInstructionView(null)}>Close</button>
                        </div>
                        <div className="odq-modal-body">
                            {instructionView.instruction_content
                                ? <pre className="odq-detail-pre odq-detail-body" style={{ maxHeight: '55vh' }}>{instructionView.instruction_content}</pre>
                                : <span style={{ color: '#9ca3af' }}>No instruction content.</span>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
